import { Fragment, PropsWithChildren } from 'react';

import { Input } from '@any-ui-react/core';
import classNames from 'classnames';

import { RuleCard, ConditionBuilderButtons, ConditionTypeRadioSelector } from './components';
import {
  useAddRule,
  useRemoveRule,
  useSubscribeConditionChange,
  useUpdateRuleField,
  useUpdateRuleOperator,
  useUpdateRuleValue,
} from './hooks';
import {
  ConditionBuilderContextProvider,
  ConditionBuilderUpdater,
  useConditionBuilderStore,
} from './store';
import { Controls, Field, OptionList, RuleCombinator, RuleGroupType } from './types';
import { ConditionBuilderError } from './types';

export interface ConditionBuilderProps {
  fields: OptionList<Field>;
  condition?: RuleGroupType;
  onConditionChange?: (condition: RuleGroupType) => void;
  errors?: ConditionBuilderError;
  hideIcon?: boolean;
  compact?: boolean;
  controlElements?: Partial<Controls>;
  hideConditionButtons?: boolean;
  disabled?: boolean;
}

export const ConditionBuilder = ({
  fields,
  condition,
  onConditionChange,
  errors,
  compact = false,
  controlElements,
  hideConditionButtons = false,
  disabled = false,
}: ConditionBuilderProps) => {
  return (
    <ConditionBuilderContextProvider
      fields={fields}
      condition={condition}
      handleConditionChange={onConditionChange}
      errors={errors}
      compact={compact}
      controlElements={controlElements}
      hideConditionButtons={hideConditionButtons}
      disabled={disabled}
    >
      <ConditionBuilderUpdater errors={errors} controlElements={controlElements} />
      <ConditionBuilderInner />
    </ConditionBuilderContextProvider>
  );
};

const ConditionBuilderFooter = ({
  children,
  compact,
}: PropsWithChildren<{ compact?: boolean }>) => {
  if (!compact) {
    return children;
  }

  return (
    <div className="rounded-b-sm border-x border-t-[0.5px] border-b p-4">
      {children}
      <ConditionTypeRadioSelector />
    </div>
  );
};

export const ConditionBuilderInner = () => {
  const combinator = useConditionBuilderStore((state) => state.condition.combinator);
  const rules = useConditionBuilderStore((state) => state.condition.rules);
  const errors = useConditionBuilderStore((state) => state.errors);
  const compact = useConditionBuilderStore((state) => state.compact);
  const disabled = useConditionBuilderStore((state) => state.disabled);
  const RuleCardComponent = useConditionBuilderStore((state) =>
    state.controlElements?.ruleCard ? state.controlElements.ruleCard : RuleCard
  );
  const hideConditionButtons = useConditionBuilderStore((state) => state.hideConditionButtons);

  const addRule = useAddRule();
  const removeRule = useRemoveRule();
  const updateRuleField = useUpdateRuleField();
  const updateRuleOperator = useUpdateRuleOperator();
  const updateRuleValue = useUpdateRuleValue();

  useSubscribeConditionChange();

  const rootErrorMessage = errors?.root?.message;

  return (
    <div
      className={classNames('relative', {
        'grid grid-cols-[1fr_1.5rem] gap-x-2': compact,
      })}
    >
      {rules.map((rule, idx) => (
        <Fragment key={rule.id}>
          <RuleCardComponent
            rule={rule}
            type={idx === 0 ? RuleCombinator.IF : combinator}
            showRemove={idx !== 0}
            onRemove={() => removeRule(rule.id)}
            onFieldChange={(field) => updateRuleField({ field, idx })}
            onOperatorChange={(operator) => updateRuleOperator({ operator, idx })}
            onValuesChange={(values) => updateRuleValue({ values, idx })}
            error={errors?.rules?.[idx] || (idx === 0 && !!rootErrorMessage)}
            compact={compact}
            disabled={disabled}
          />
          {!compact && !hideConditionButtons && (
            <div className="border-gray-2 ml-8 block h-4 w-0 border" />
          )}
        </Fragment>
      ))}
      <ConditionBuilderFooter compact={compact}>
        {!hideConditionButtons && (
          <ConditionBuilderButtons
            addRule={addRule}
            ruleCount={rules.length}
            combinator={combinator}
            compact={compact}
            disabled={disabled}
          />
        )}
        {rootErrorMessage && !compact && (
          <Input.Error className="mt-1">{rootErrorMessage}</Input.Error>
        )}
      </ConditionBuilderFooter>
    </div>
  );
};
